import React, { Component, useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Grid, Row, Col } from 'react-flexbox-grid'
import { Divider, DatePicker, Button, Table, Modal, Spin, message, Tooltip, Input, Space, TimePicker } from 'antd'
import { PictureOutlined, EnvironmentOutlined, LoadingOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import moment from 'moment'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { getUser } from '../../../reducers'
import { firestore, db, storage } from '../../../config/Fire'
import * as Constants from '../../../constants'
import './styles.css'

const { RangePicker } = DatePicker

// Componente para mostrar miniatura de imagen
const ImageThumbnail = ({ imagePath, onImageClick }) => {
	const [imageUrl, setImageUrl] = useState(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (!imagePath) {
			setLoading(false)
			return
		}

		storage.ref().child(imagePath).getDownloadURL()
			.then(url => {
				setImageUrl(url)
				setLoading(false)
			})
			.catch(err => {
				console.log('Error loading thumbnail: ' + JSON.stringify(err))
				setImageUrl(null)
				setLoading(false)
			})
	}, [imagePath])

	if (!imagePath) {
		return <PictureOutlined style={{ fontSize: '20px', cursor: 'pointer', color: '#1890ff' }} />
	}

	if (loading) {
		return <Spin size="small" />
	}

	if (!imageUrl) {
		return <PictureOutlined style={{ fontSize: '20px', cursor: 'pointer', color: '#1890ff' }} />
	}

	return (
		<img
			src={imageUrl}
			alt="Miniatura"
			style={{
				width: '50px',
				height: '50px',
				objectFit: 'cover',
				cursor: 'pointer',
				borderRadius: '4px',
				border: '1px solid #d9d9d9'
			}}
			onClick={() => onImageClick && onImageClick(imagePath)}
		/>
	)
}

class Registers extends Component {
	state = {
		dateRange: null,
		records: [],
		medicalLicenses: [],
		users: {},
		projects: {},
		loading: false,
		tableData: [],
		imageModalVisible: false,
		imageModalUrl: '',
		imageModalLoading: true,
		mapModalVisible: false,
		mapCoordinates: null,
		mapDate: new Date(),
		mapRecords: [],
		mapLoading: false,
		mapInstance: null,
		mapMarkers: [],
		imageUrlsCache: {}, // Cache para URLs de imágenes
		searchText: '',
		searchedColumn: '',
		editTimeModalVisible: false,
		recordToEdit: null,
		newTimeValue: null,
		editingTimeLoading: false
	}

	componentDidMount() {
		this.loadUsers()
		this.loadProjects()
	}

	loadUsers = () => {
		db.ref('users').once('value')
			.then(usersSnap => {
				const usersLoaded = {}
				if (usersSnap.exists()) {
					usersSnap.forEach(userSnap => {
						const user = userSnap.val()
						// Seleccionar únicamente usuarios de tipo CONSTRUCTION
						// Agrupar por DNI en lugar de userId
						if (user.id && user.userType === 'CONSTRUCTION' && user.dni) {
							// EXCEPCION: NO MOSTRAR ESTE USUARIO PORQUE ES DE PRUEBA
							if (user.id === '6GTNp4azJWh4miIsVBRrNP1tKqq2') return

							// Usar DNI como clave en lugar de userId, convertir a string para consistencia
							const dniKey = String(user.dni)
							usersLoaded[dniKey] = user
						}
					})
				}
				this.setState({ users: usersLoaded })
			})
			.catch(err => {
				console.log('Error getting users: ' + JSON.stringify(err))
				message.error('Error al cargar usuarios')
			})
	}

	loadProjects = () => {
		db.ref(Constants.MAIN_NODES.PROJECTS).once('value')
			.then(projectsSnap => {
				const projectsLoaded = {}
				if (projectsSnap.exists()) {
					projectsSnap.forEach(projectSnap => {
						const project = projectSnap.val()
						// Cargar todos los proyectos, sin filtrar por estado
						if (project && project.id) {
							projectsLoaded[project.id] = project
						}
					})
				}
				this.setState({ projects: projectsLoaded })
			})
			.catch(err => {
				console.log('Error getting projects: ' + JSON.stringify(err))
				message.error('Error al cargar proyectos')
			})
	}

	// Calcular distancia entre dos puntos en kilómetros (fórmula de Haversine)
	calculateDistance = (lat1, lon1, lat2, lon2) => {
		if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity

		const R = 6371 // Radio de la Tierra en kilómetros
		const dLat = this.toRad(lat2 - lat1)
		const dLon = this.toRad(lon2 - lon1)
		const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
			Math.sin(dLon / 2) * Math.sin(dLon / 2)
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
		const distance = R * c
		return distance
	}

	toRad = (degrees) => {
		return degrees * (Math.PI / 180)
	}

	// Detectar proyecto basado en latitud y longitud del registro
	detectProject = (latitude, longitude) => {
		const { projects } = this.state
		if (!latitude || !longitude || !projects || Object.keys(projects).length === 0) {
			return null
		}

		let closestProject = null
		let minDistance = Constants.PROJECT_DETECTION_RANGE_KM

		Object.keys(projects).forEach(projectId => {
			const project = projects[projectId]
			if (project.latitude && project.longitude) {
				const distance = this.calculateDistance(latitude, longitude, project.latitude, project.longitude)
				if (distance < minDistance) {
					minDistance = distance
					closestProject = project
				}
			}
		})

		return closestProject
	}

	// Meeting points are used only as gathering locations, never as the real worksite
	MEETING_POINT_NAMES = ['Desvio Arijon', 'Coinauto', 'Edificio S&D', 'Galpon Diego']

	/**
	 * Detects the legacy mobile-app bug where the real worksite project was
	 * incorrectly stored in `subProjectId` instead of `projectSelected`.
	 * Returns true only when ALL of the following are true:
	 *  1. The project referenced by subProjectId is NOT a meeting point.
	 *  2. The record's coordinates are within PROJECT_DETECTION_RANGE_KM of that same project.
	 * @param {string} subProjectId
	 * @param {number} latitude
	 * @param {number} longitude
	 * @returns {boolean}
	 */
	isLegacySubProjectBug = (subProjectId, latitude, longitude) => {
		const { projects } = this.state
		const subProject = projects && projects[subProjectId]
		if (!subProject) return false
		// If the referenced project is a meeting point it is not the bug case
		if (this.MEETING_POINT_NAMES.includes(subProject.name)) return false
		// Need both record coordinates and project coordinates to compare
		if (!latitude || !longitude || !subProject.latitude || !subProject.longitude) return false
		const distance = this.calculateDistance(latitude, longitude, subProject.latitude, subProject.longitude)
		return distance <= Constants.PROJECT_DETECTION_RANGE_KM
	}

	/**
	 * Detects the inverse legacy mobile-app bug: a non-meeting-point project was
	 * stored in `projectSelected` (with no subProjectId) but the record's coordinates
	 * are outside the range of that project, meaning it should have been a sub-project.
	 * Returns true only when ALL of the following are true:
	 *  1. The project referenced by projectSelected is NOT a meeting point.
	 *  2. The record's coordinates are NOT within PROJECT_DETECTION_RANGE_KM of that project.
	 * @param {string} projectSelectedId
	 * @param {number} latitude
	 * @param {number} longitude
	 * @returns {boolean}
	 */
	isLegacyProjectSelectedBug = (projectSelectedId, latitude, longitude) => {
		const { projects } = this.state
		const selectedProject = projects && projects[projectSelectedId]
		if (!selectedProject) return false
		// Meeting points are always legitimate as the main project
		if (this.MEETING_POINT_NAMES.includes(selectedProject.name)) return false
		// If the project has no coordinates we cannot determine proximity — not a bug
		if (!selectedProject.latitude || !selectedProject.longitude) return false
		// If the record has no coordinates assume it was legitimately selected
		if (!latitude || !longitude) return false
		const distance = this.calculateDistance(latitude, longitude, selectedProject.latitude, selectedProject.longitude)
		// Outside range → the project was saved incorrectly as main project
		return distance > Constants.PROJECT_DETECTION_RANGE_KM
	}

	// Obtener horas normales del día según WEEK_DAYS_WORK_HOURS
	getNormalHoursForDay = (dateLong) => {
		const date = new Date(dateLong)
		const dayOfWeek = date.getDay() // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado

		// Mapear día de la semana a índice del array (0 = Lunes, 6 = Domingo)
		const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1

		if (dayIndex >= 0 && dayIndex < Constants.WEEK_DAYS_WORK_HOURS.length) {
			return Constants.WEEK_DAYS_WORK_HOURS[dayIndex].hours
		}
		return 0
	}

	// Obtener nombre del día de la semana
	getDayName = (dateLong) => {
		const date = new Date(dateLong)
		const dayOfWeek = date.getDay()
		const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1

		if (dayIndex >= 0 && dayIndex < Constants.WEEK_DAYS_WORK_HOURS.length) {
			return Constants.WEEK_DAYS_WORK_HOURS[dayIndex].day
		}
		return '--'
	}

	/**
	 * Applies the 40-minute rounding rule to overtime hours.
	 * If the fractional minutes are <= 40, truncate (don't count the partial hour).
	 * If the fractional minutes are > 40, round up to the next full hour.
	 * @param {number} hours - Raw overtime hours as a decimal
	 * @returns {number} Rounded overtime hours as a whole number
	 */
	applyOvertimeRounding = (hours) => {
		// Rounding rule disabled by client request, return raw hours
		// const wholeHours = Math.floor(hours)
		// const fractionalMinutes = (hours - wholeHours) * 60
		// return fractionalMinutes > 40 ? wholeHours + 1 : wholeHours
		return hours
	}

	handleDateRangeChange = (dates) => {
		if (dates && dates[0] && dates[1]) {
			const startDate = moment(dates[0])
			const endDate = moment(dates[1])
			const daysDifference = endDate.diff(startDate, 'days')

			if (daysDifference > 31) {
				message.warning('El rango de fechas seleccionado no puede exceder los 31 días')
				this.setState({ dateRange: null, tableData: [], records: [] })
				return
			}
		}

		// Si se limpia el rango de fechas, también limpiar la tabla
		if (!dates || !dates[0] || !dates[1]) {
			this.setState({ dateRange: null, tableData: [], records: [] })
			return
		}

		this.setState({ dateRange: dates })
	}

	handleSearch = async () => {
		const { dateRange } = this.state
		if (!dateRange || !dateRange[0] || !dateRange[1]) {
			message.warning('Por favor, seleccione un rango de fechas')
			return
		}

		this.setState({ loading: true })

		const startDate = moment(dateRange[0]).startOf('day').valueOf()
		const endDate = moment(dateRange[1]).endOf('day').valueOf()

		try {
			const licensesSnap = await firestore.collection('medical_license').get()
			const medicalLicenses = []
			if (!licensesSnap.empty) {
				licensesSnap.forEach(doc => {
					const data = doc.data()
					const licStartStr = data.startDate
					const licEndStr = data.endDate
					if (licStartStr && licEndStr) {
						// Fallback to various formats to prevent moment.js depreciation warnings
						const formats = ['DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY-MM-DDTHH:mm:ss.SSSZ']
						const licStart = moment(licStartStr, formats).startOf('day').valueOf()
						const licEnd = moment(licEndStr, formats).endOf('day').valueOf()
						if (licEnd >= startDate && licStart <= endDate && data.dni) {
							medicalLicenses.push({
								...data,
								licStart,
								licEnd
							})
						}
					}
				})
			}

			firestore.collection('records')
				.where('dateLong', '>=', startDate)
				.where('dateLong', '<=', endDate)
				.orderBy('dateLong')
				.get()
				.then(querySnapshot => {
					const recordsLoaded = []
					if (!querySnapshot.empty) {
						querySnapshot.forEach(doc => {
							const record = doc.data()
							record.id = doc.id
							recordsLoaded.push(record)
						})
					}
					this.setState({ records: recordsLoaded, medicalLicenses }, () => {
						this.processRecords()
					})
				})
				.catch(err => {
					console.log('Error getting records: ' + JSON.stringify(err))
					if (err.code === 'failed-precondition') {
						message.error('Error: Se requiere crear un índice en Firestore. Por favor, contacte al administrador.')
					} else {
						message.error('Error al cargar registros')
					}
					this.setState({ loading: false })
				})
		} catch (err) {
			console.log('Error getting medical licenses: ' + JSON.stringify(err))
			message.error('Error al cargar licencias médicas')
			this.setState({ loading: false })
		}
	}

	processRecords = () => {
		const { records, users, projects, medicalLicenses, dateRange } = this.state

		// Agrupar registros por DNI en lugar de userId y detectar proyectos
		const recordsByDni = {}
		records.forEach(record => {
			// Usar el campo dni del registro, convertir a string para consistencia
			const dni = record.dni ? String(record.dni) : null
			if (!dni) {
				// Si el registro no tiene DNI, saltarlo
				return
			}

			// Detectar proyecto si es CHECK_IN, CHECK_OUT o RAINY_DAY
			if (record.recordType === 'CHECK_IN' || record.recordType === 'CHECK_OUT' || record.recordType === 'RAINY_DAY') {
				let project = null
				let mainProject = null
				let subProject = null

				// 1. Prioridad máxima: sub-proyecto explícito (projectSelected es solo punto de encuentro)
				if (record.subProjectId != null && projects && projects[record.subProjectId] && projects[record.subProjectId].isActive === true) {
					// Legacy bug fix: the mobile app incorrectly stored the real project in subProjectId
					// when the user was near a non-meeting-point project. In that case, treat subProjectId
					// as the main project so it renders in the "Proyecto Detectado" column, not "SubProyecto".
					const hasLegacyBug = this.isLegacySubProjectBug(
						record.subProjectId,
						record.latitude,
						record.longitude
					)

					if (hasLegacyBug) {
						// Promote subProjectId → main project (subProject stays null)
						project = projects[record.subProjectId]
						mainProject = projects[record.subProjectId]
					} else {
						project = projects[record.subProjectId]
						subProject = projects[record.subProjectId]
						if (record.projectSelected && projects && projects[record.projectSelected] && projects[record.projectSelected].isActive === true) {
							mainProject = projects[record.projectSelected]
						}
					}
				} else if (record.subProjectId == null && record.projectSelected && projects && projects[record.projectSelected]) {
					// 2. Sin sub-proyecto: usar projectSelected si está activo
					const selectedProject = projects[record.projectSelected]
					if (selectedProject.isActive === true) {
						// Legacy bug fix (inverse): a non-meeting-point project stored in projectSelected
						// while coordinates are outside its range → it should be a sub-project.
						const hasLegacyBug = this.isLegacyProjectSelectedBug(
							record.projectSelected,
							record.latitude,
							record.longitude
						)

						if (hasLegacyBug) {
							// Demote projectSelected → sub-project (mainProject stays null)
							project = selectedProject
							subProject = selectedProject
						} else {
							project = selectedProject
							mainProject = selectedProject
						}
					}
				}

				// 3. Fallback: Si no hay proyecto seleccionado válido, detectar por cercanía si hay coordenadas
				if (!project && record.latitude && record.longitude) {
					project = this.detectProject(record.latitude, record.longitude)
					mainProject = project
				}

				if (project) {
					record.detectedProject = project
				}
				if (mainProject) {
					record.detectedMainProject = mainProject
				}
				if (subProject) {
					record.detectedSubProject = subProject
				}
			}

			if (!recordsByDni[dni]) {
				recordsByDni[dni] = []
			}
			recordsByDni[dni].push(record)
		})

		// Inyectar registros virtuales de licencias médicas
		if (medicalLicenses && medicalLicenses.length > 0 && dateRange) {
			const searchStart = moment(dateRange[0]).startOf('day').valueOf()
			const searchEnd = moment(dateRange[1]).endOf('day').valueOf()

			medicalLicenses.forEach(license => {
				const dni = String(license.dni)
				if (!users[dni]) return

				if (!recordsByDni[dni]) {
					recordsByDni[dni] = []
				}

				const start = Math.max(searchStart, license.licStart)
				const end = Math.min(searchEnd, license.licEnd)

				const current = moment(start)
				while (current.valueOf() <= end) {
					const virtualRecord = {
						id: `mock-lic-${dni}-${current.valueOf()}`,
						recordType: 'MEDICAL_LICENSE',
						dateLong: current.startOf('day').valueOf(),
						dni: dni,
						observations: license.observation || 'Licencia Médica'
					}
					const exists = recordsByDni[dni].some(r => r.recordType === 'MEDICAL_LICENSE' && moment(r.dateLong).isSame(current, 'day'))
					if (!exists) {
						recordsByDni[dni].push(virtualRecord)
					}
					current.add(1, 'days')
				}
			})
		}

		// Crear datos para la tabla
		const allDnis = new Set([...Object.keys(users), ...Object.keys(recordsByDni)])
		const tableData = Array.from(allDnis).map(dni => {
			const userRecords = recordsByDni[dni] || []
			const user = users[dni] || {}

			// Ordenar registros por fecha
			userRecords.sort((a, b) => a.dateLong - b.dateLong)

			// Calcular horas trabajadas, horas extras y horas traslado
			const { workedHours, extraHours, travelHours, status } = this.calculateWorkedHours(userRecords)

			// Detectar si algún registro no tiene proyecto asociado
			const hasRecordsWithoutProject = userRecords.some(r =>
				(r.recordType === 'CHECK_IN' || r.recordType === 'CHECK_OUT' || r.recordType === 'RAINY_DAY') &&
				!r.detectedProject
			)

			return {
				key: dni,
				dni: dni,
				name: user.name || '--',
				lastName: user.lastName || '',
				email: user.email || '--',
				category: user.category || '--',
				isSadeEmployee: user.isSadeEmployee,
				workedHours: workedHours,
				extraHours: extraHours,
				travelHours: travelHours,
				status: status,
				hasRecordsWithoutProject: hasRecordsWithoutProject,
				records: userRecords
			}
		})

		const filteredTableData = tableData.filter(item => {
			const user = users[item.key]
			if (!user) return item.records && item.records.length > 0
			return user.enabled !== false || (item.records && item.records.length > 0)
		})

		this.setState({ tableData: filteredTableData, loading: false })
	}

	calculateWorkedHours = (records) => {
		if (!records || records.length === 0) {
			return { workedHours: 0, extraHours: 0, travelHours: 0, status: 'red' }
		}

		// Agrupar registros por día
		const recordsByDay = {}
		records.forEach(record => {
			const dayKey = moment(record.dateLong).format('YYYY-MM-DD')
			if (!recordsByDay[dayKey]) {
				recordsByDay[dayKey] = []
			}
			recordsByDay[dayKey].push(record)
		})

		let totalHours = 0
		let totalExtraHours = 0
		let totalTravelHours = 0
		let hasMissingCheckOut = false

		// Calcular horas por cada día
		Object.keys(recordsByDay).forEach(dayKey => {
			const dayRecords = recordsByDay[dayKey]
			// Ordenar por fecha
			dayRecords.sort((a, b) => a.dateLong - b.dateLong)

			let checkInTime = null
			let dayWorkedHours = 0
			let dayTravelHours = 0

			// Medical license takes priority: ignore all other record types for this day
			const dayHasMedicalLicense = dayRecords.some(r => r.recordType === 'MEDICAL_LICENSE')

			if (dayHasMedicalLicense) {
				dayWorkedHours = this.getNormalHoursForDay(dayRecords[0].dateLong)
			} else {
				dayRecords.forEach(record => {
					if (record.recordType === 'CHECK_IN') {
						checkInTime = record.dateLong
						// Usar el proyecto ya detectado en processRecords
						if (record.detectedProject && record.detectedProject.travelHours !== null && record.detectedProject.travelHours !== undefined) {
							dayTravelHours += (record.detectedProject.travelHours || 0)
						}
					} else if (record.recordType === 'CHECK_OUT' && checkInTime !== null) {
						const hours = (record.dateLong - checkInTime) / (1000 * 60 * 60)
						dayWorkedHours += hours
						checkInTime = null
					} else if (record.recordType === 'RAINY_DAY') {
						dayWorkedHours += 2.5
						if (record.detectedProject && record.detectedProject.travelHours !== null && record.detectedProject.travelHours !== undefined) {
							dayTravelHours += (record.detectedProject.travelHours || 0)
						}
					}
				})
			}

			if (checkInTime !== null) {
				hasMissingCheckOut = true
			}

			// Calcular horas extras para este día
			const normalHours = this.getNormalHoursForDay(dayRecords[0].dateLong)
			const dateObj = new Date(dayRecords[0].dateLong)
			const isWeekend = (dateObj.getDay() === 0 || dateObj.getDay() === 6) // Domingo = 0, Sábado = 6

			if (isWeekend) {
				totalExtraHours += this.applyOvertimeRounding(dayWorkedHours)
			} else {
				totalHours += dayWorkedHours
				if (dayWorkedHours > normalHours) {
					totalExtraHours += this.applyOvertimeRounding(dayWorkedHours - normalHours)
				}
			}

			// Sumar horas de traslado del día
			totalTravelHours += dayTravelHours
		})

		let status = 'green'
		if (hasMissingCheckOut) {
			status = 'yellow'
		}

		return {
			workedHours: parseFloat(totalHours.toFixed(2)),
			extraHours: parseFloat(totalExtraHours.toFixed(2)),
			travelHours: parseFloat(totalTravelHours.toFixed(2)),
			status
		}
	}

	// Obtener datos diarios para Excel
	getDailyDataForExcel = (records) => {
		// Agrupar registros por día
		const recordsByDay = {}
		records.forEach(record => {
			const dayKey = moment(record.dateLong).format('YYYY-MM-DD')
			if (!recordsByDay[dayKey]) {
				recordsByDay[dayKey] = []
			}
			recordsByDay[dayKey].push(record)
		})

		const dailyData = []

		// Calcular horas por cada día
		Object.keys(recordsByDay).forEach(dayKey => {
			const dayRecords = recordsByDay[dayKey]
			// Ordenar por fecha
			dayRecords.sort((a, b) => a.dateLong - b.dateLong)

			let checkInTime = null
			let dayWorkedHours = 0
			let dayTravelHours = 0
			let firstCheckInProject = null
			let firstCheckInMainProject = null
			let firstCheckInSubProject = null
			let firstCheckInTime = null
			let lastCheckOutTime = null
			let isRainyDay = false
			let isMedicalLicense = false
			let dailyObservations = []

			// Medical license takes priority: collect observations from all records
			// but ignore hours from check-in/out/rainy if a medical license exists for this day
			const dayHasMedicalLicense = dayRecords.some(r => r.recordType === 'MEDICAL_LICENSE')

			dayRecords.forEach(record => {
				if (record.observations && String(record.observations).trim() !== '') {
					let prefix = ''
					if (record.recordType === 'CHECK_IN') prefix = 'Entrada: '
					else if (record.recordType === 'CHECK_OUT') prefix = 'Salida: '
					else if (record.recordType === 'RAINY_DAY') prefix = 'Lluvia: '

					const obsData = { prefix, text: String(record.observations).trim() }
					const alreadyExists = dailyObservations.some(o => o.prefix === prefix && o.text === obsData.text)
					if (!alreadyExists) {
						dailyObservations.push(obsData)
					}
				}

				if (record.recordType === 'MEDICAL_LICENSE') {
					isMedicalLicense = true
				} else if (!dayHasMedicalLicense) {
					// Only process non-license records if there is no medical license this day
					if (record.recordType === 'CHECK_IN') {
						if (checkInTime === null) {
							// Guardar el proyecto del primer CHECK_IN del día
							firstCheckInProject = record.detectedProject
							firstCheckInMainProject = record.detectedMainProject
							firstCheckInSubProject = record.detectedSubProject
							// Guardar la hora del primer ingreso
							if (firstCheckInTime === null) {
								firstCheckInTime = record.dateLong
							}
						}
						checkInTime = record.dateLong
						// Usar el proyecto ya detectado en processRecords
						if (record.detectedProject && record.detectedProject.travelHours !== null && record.detectedProject.travelHours !== undefined) {
							dayTravelHours += (record.detectedProject.travelHours || 0)
						}
					} else if (record.recordType === 'CHECK_OUT') {
						// Guardar la hora del último egreso
						lastCheckOutTime = record.dateLong

						if (checkInTime !== null) {
							const hours = (record.dateLong - checkInTime) / (1000 * 60 * 60)
							dayWorkedHours += hours
							checkInTime = null
						}
					} else if (record.recordType === 'RAINY_DAY') {
						isRainyDay = true
						dayWorkedHours += 2.5

						if (firstCheckInProject === null) {
							firstCheckInProject = record.detectedProject
						}
						if (firstCheckInMainProject === null) {
							firstCheckInMainProject = record.detectedMainProject
						}
						if (firstCheckInSubProject === null) {
							firstCheckInSubProject = record.detectedSubProject
						}
						if (firstCheckInTime === null) {
							firstCheckInTime = record.dateLong
						}
						lastCheckOutTime = record.dateLong

						if (record.detectedProject && record.detectedProject.travelHours !== null && record.detectedProject.travelHours !== undefined) {
							dayTravelHours += (record.detectedProject.travelHours || 0)
						}
					}
				}
			})

			// If medical license, override hours with the standard normal hours for the day
			if (dayHasMedicalLicense) {
				dayWorkedHours = this.getNormalHoursForDay(dayRecords[0].dateLong)
			}

			// Calcular horas extras para este día
			const normalHours = this.getNormalHoursForDay(dayRecords[0].dateLong)
			let dayExtraHours = 0
			let finalWorkedHours = dayWorkedHours

			const dateObj = new Date(dayRecords[0].dateLong)
			const isWeekend = (dateObj.getDay() === 0 || dateObj.getDay() === 6)

			if (isWeekend) {
				dayExtraHours = this.applyOvertimeRounding(dayWorkedHours)
				finalWorkedHours = 0
			} else {
				if (dayWorkedHours > normalHours) {
					dayExtraHours = this.applyOvertimeRounding(dayWorkedHours - normalHours)
				}
			}

			// Obtener fecha formateada
			const dateFormatted = `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`

			const formatTime = (timestamp) => {
				if (!timestamp) return '--'
				const d = new Date(timestamp)
				return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
			}

			let projectLabel = '--'
			if (firstCheckInMainProject && firstCheckInMainProject.name) {
				projectLabel = firstCheckInMainProject.name
			} else if (firstCheckInSubProject) {
				projectLabel = '--'
			} else if (firstCheckInProject && firstCheckInProject.name) {
				projectLabel = firstCheckInProject.name
			}

			let subProjectLabel = '-'
			if (firstCheckInSubProject && firstCheckInSubProject.name) {
				subProjectLabel = firstCheckInSubProject.name
			}

			let observationsValue = '--'
			if (dailyObservations.length > 0) {
				const richText = []
				dailyObservations.forEach((obs, index) => {
					if (obs.prefix) {
						richText.push({ text: obs.prefix, font: { bold: true } })
					}
					const suffix = (index < dailyObservations.length - 1) ? ' | ' : ''
					richText.push({ text: `${obs.text}${suffix}` })
				})
				observationsValue = { richText }
			}

			dailyData.push({
				date: dateFormatted,
				checkInTime: formatTime(firstCheckInTime),
				checkOutTime: formatTime(lastCheckOutTime),
				project: projectLabel,
				subProject: subProjectLabel,
				category: dayRecords[0]?.category || '--',
				workedHours: parseFloat(finalWorkedHours.toFixed(2)),
				extraHours: parseFloat(dayExtraHours.toFixed(2)),
				travelHours: parseFloat(dayTravelHours.toFixed(2)),
				isRainyDay: isRainyDay,
				isMedicalLicense: isMedicalLicense,
				missingProject: !firstCheckInProject && !isMedicalLicense,
				observations: observationsValue
			})
		})

		return dailyData
	}

	exportToExcel = async () => {
		const { dateRange, tableData, searchText, searchedColumn } = this.state

		// Validar que haya fechas seleccionadas
		if (!dateRange || !dateRange[0] || !dateRange[1]) {
			message.warning('Por favor, seleccione un rango de fechas antes de descargar')
			return
		}

		// Validar que haya datos para exportar
		if (!tableData || tableData.length === 0) {
			message.warning('No hay datos para exportar. Por favor, realice una búsqueda primero')
			return
		}

		// Aplicar filtrado si existe para que el Excel coincida con lo que se ve en la tabla
		let filteredData = tableData
		if (searchText) {
			filteredData = tableData.filter(record => {
				if (searchedColumn === 'name') {
					const fullName = `${record.name || ''} ${record.lastName || ''}`.trim().toLowerCase()
					return fullName.includes(searchText.toLowerCase())
				}
				return record[searchedColumn]
					? record[searchedColumn].toString().toLowerCase().includes(searchText.toLowerCase())
					: false
			})
		}

		// Crear workbook con ExcelJS
		const workbook = new ExcelJS.Workbook()
		const worksheet = workbook.addWorksheet('Registros')

		// Definir columnas con ancho óptimo
		const COLUMN_HEADERS = [
			{ header: 'Nombre', key: 'nombre', width: 28 },
			{ header: 'Fecha', key: 'fecha', width: 14 },
			{ header: 'Hora Ingreso', key: 'horaIngreso', width: 14 },
			{ header: 'Hora Egreso', key: 'horaEgreso', width: 14 },
			{ header: 'Día de lluvia', key: 'rainy', width: 14 },
			{ header: 'Licencia Médica', key: 'licencia', width: 14 },
			{ header: 'Proyecto', key: 'proyecto', width: 26 },
			{ header: 'Subproyecto', key: 'subproyecto', width: 26 },
			{ header: 'Categoría', key: 'categoria', width: 18 },
			{ header: 'Horas trabajadas', key: 'horasTrabajadas', width: 18 },
			{ header: 'Horas extras', key: 'horasExtras', width: 14 },
			{ header: 'Horas traslado', key: 'horasTraslado', width: 14 },
			{ header: 'Empleado Grupo SADE', key: 'isSadeEmployee', width: 22 },
			{ header: 'Observaciones', key: 'observaciones', width: 60 },
		]
		worksheet.columns = COLUMN_HEADERS

		// Estilos del encabezado
		const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF263238' } }
		const HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 }
		const HEADER_ALIGNMENT = { horizontal: 'center', vertical: 'middle' }
		const HEADER_BORDER = {
			top: { style: 'thin', color: { argb: 'FF455A64' } },
			bottom: { style: 'medium', color: { argb: 'FF455A64' } },
			left: { style: 'thin', color: { argb: 'FF455A64' } },
			right: { style: 'thin', color: { argb: 'FF455A64' } }
		}

		// Estilos de fila normal
		const NORMAL_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
		const NORMAL_BORDER = {
			top: { style: 'hair', color: { argb: 'FFD0D0D0' } },
			bottom: { style: 'hair', color: { argb: 'FFD0D0D0' } },
			left: { style: 'hair', color: { argb: 'FFD0D0D0' } },
			right: { style: 'hair', color: { argb: 'FFD0D0D0' } }
		}

		// Estilos de fila con proyecto faltante (ambar)
		const AMBER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8E1' } }
		const AMBER_BORDER = {
			top: { style: 'thin', color: { argb: 'FFE6A817' } },
			bottom: { style: 'thin', color: { argb: 'FFE6A817' } },
			left: { style: 'medium', color: { argb: 'FFE6A817' } },
			right: { style: 'medium', color: { argb: 'FFE6A817' } }
		}

		// Aplicar estilos a la fila de encabezado
		const headerRow = worksheet.getRow(1)
		headerRow.height = 22
		headerRow.eachCell(cell => {
			cell.fill = HEADER_FILL
			cell.font = HEADER_FONT
			cell.alignment = HEADER_ALIGNMENT
			cell.border = HEADER_BORDER
		})

		// Agregar filas de datos con estilos
		filteredData.forEach(userData => {
			const userName = `${userData.name} ${userData.lastName}`.trim()
			const dailyData = this.getDailyDataForExcel(userData.records)
			const isSadeEmployeeText = userData.isSadeEmployee === true ? 'SI' : (userData.isSadeEmployee === false ? 'NO' : '-')

			dailyData.forEach(dayData => {
				const catKey = dayData.category !== '--' && dayData.category !== '-' && dayData.category !== '' ? dayData.category : (userData.category || '-')
				const categoriaLabel = Constants.OPERATOR_CATEGORIES[catKey] || catKey

				const row = worksheet.addRow({
					nombre: userName,
					fecha: dayData.date,
					horaIngreso: dayData.checkInTime,
					horaEgreso: dayData.checkOutTime,
					rainy: dayData.isRainyDay ? 'SI' : 'NO',
					licencia: dayData.isMedicalLicense ? 'SI' : 'NO',
					proyecto: dayData.project,
					subproyecto: dayData.subProject,
					categoria: categoriaLabel,
					horasTrabajadas: dayData.workedHours,
					horasExtras: dayData.extraHours,
					horasTraslado: dayData.travelHours,
					isSadeEmployee: isSadeEmployeeText,
					observaciones: dayData.observations
				})

				// Aplicar estilos según si le falta proyecto
				const fill = dayData.missingProject ? AMBER_FILL : NORMAL_FILL
				const border = dayData.missingProject ? AMBER_BORDER : NORMAL_BORDER

				row.eachCell((cell, colNumber) => {
					cell.fill = fill
					cell.border = border

					const colKey = worksheet.getColumn(colNumber).key
					if (['isSadeEmployee', 'rainy', 'licencia'].includes(colKey)) {
						cell.alignment = { vertical: 'middle', horizontal: 'center' }
					} else {
						cell.alignment = { vertical: 'middle' }
					}
				})
			})
		})

		// Generar el buffer del workbook y disparar la descarga
		const buffer = await workbook.xlsx.writeBuffer()
		const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
		const startDate = moment(dateRange[0]).format('DD-MM-YYYY')
		const endDate = moment(dateRange[1]).format('DD-MM-YYYY')
		saveAs(blob, `${startDate}_${endDate}_SD.xlsx`)
		message.success('Excel descargado exitosamente')
	}

	getImageUrl = (imagePath, callback) => {
		if (!imagePath) {
			callback(null)
			return
		}

		// Verificar si ya está en cache
		if (this.state.imageUrlsCache[imagePath]) {
			callback(this.state.imageUrlsCache[imagePath])
			return
		}

		// Cargar la URL desde Firebase Storage
		storage.ref().child(imagePath).getDownloadURL()
			.then(url => {
				// Guardar en cache
				this.setState(prevState => ({
					imageUrlsCache: {
						...prevState.imageUrlsCache,
						[imagePath]: url
					}
				}))
				callback(url)
			})
			.catch(err => {
				console.log('Error loading image: ' + JSON.stringify(err))
				callback(null)
			})
	}

	showImageModal = (imagePath) => {
		if (!imagePath) {
			message.warning('No hay imagen disponible')
			return
		}

		this.setState({
			imageModalVisible: true,
			imageModalUrl: '',
			imageModalLoading: true
		})

		storage.ref().child(imagePath).getDownloadURL()
			.then(url => {
				this.setState({
					imageModalUrl: url,
					imageModalLoading: false
				})
			})
			.catch(err => {
				console.log('Error loading image: ' + JSON.stringify(err))
				message.error('Error al cargar la imagen')
				this.setState({
					imageModalVisible: false,
					imageModalLoading: false
				})
			})
	}

	closeImageModal = () => {
		this.setState({
			imageModalVisible: false,
			imageModalUrl: '',
			imageModalLoading: true
		})
	}

	showMapModal = (latitude, longitude) => {
		if (!latitude || !longitude) {
			message.warning('No hay ubicación disponible')
			return
		}

		this.setState({
			mapModalVisible: true,
			mapCoordinates: { latitude, longitude },
			mapDate: null
		})
	}

	closeSingleLocationModal = () => {
		this.setState({
			mapModalVisible: false,
			mapCoordinates: null
		})
	}

	formatDate = (dateLong) => {
		return moment(dateLong).format('HH:mm DD/MM/YYYY')
	}

	getRecordTypeLabel = (recordType) => {
		if (recordType === 'CHECK_IN') return 'Ingreso'
		if (recordType === 'CHECK_OUT') return 'Egreso'
		if (recordType === 'RAINY_DAY') return 'Día de lluvia'
		if (recordType === 'MEDICAL_LICENSE') return 'Licencia Médica'
		return recordType
	}

	formatDateForMap = (dateLong) => {
		const date = new Date(dateLong)
		const hours = String(date.getHours()).padStart(2, '0')
		const minutes = String(date.getMinutes()).padStart(2, '0')
		const day = String(date.getDate()).padStart(2, '0')
		const month = String(date.getMonth() + 1).padStart(2, '0')
		const year = date.getFullYear()
		return `${hours}:${minutes} ${day}/${month}/${year}`
	}

	getStartOfDay = (date) => {
		const d = new Date(date)
		d.setHours(0, 0, 0, 0)
		return d.getTime()
	}

	getEndOfDay = (date) => {
		const d = new Date(date)
		d.setHours(23, 59, 59, 999)
		return d.getTime()
	}

	loadGoogleMapsScript = () => {
		return new Promise((resolve, reject) => {
			if (window.google && window.google.maps) {
				resolve()
				return
			}

			if (document.querySelector('script[src*="maps.googleapis.com"]')) {
				const checkInterval = setInterval(() => {
					if (window.google && window.google.maps) {
						clearInterval(checkInterval)
						resolve()
					}
				}, 100)
				return
			}

			const script = document.createElement('script')
			script.src = `https://maps.googleapis.com/maps/api/js?key=${Constants.GOOGLE_API.MAPS_API_KEY}&libraries=places`
			script.async = true
			script.defer = true
			script.onload = () => resolve()
			script.onerror = () => reject(new Error('Error al cargar Google Maps'))
			document.head.appendChild(script)
		})
	}

	initializeMap = () => {
		if (!this.mapContainerRef) {
			setTimeout(() => this.initializeMap(), 100)
			return
		}

		this.loadGoogleMapsScript()
			.then(() => {
				const map = new window.google.maps.Map(this.mapContainerRef, {
					zoom: 10,
					center: { lat: -34.6037, lng: -58.3816 },
					mapTypeId: 'roadmap'
				})

				this.setState({ mapInstance: map }, () => {
					// Renderizar marcadores si ya hay registros cargados
					if (this.state.mapRecords && this.state.mapRecords.length > 0) {
						this.renderMarkers(this.state.mapRecords)
					}
				})
			})
			.catch(err => {
				console.log('Error loading Google Maps: ' + JSON.stringify(err))
				message.error('Error al cargar el mapa')
			})
	}

	renderMarkers = (records) => {
		const { mapInstance, users } = this.state

		if (!mapInstance) return

		// Limpiar marcadores anteriores SIEMPRE
		if (this.state.mapMarkers && this.state.mapMarkers.length > 0) {
			this.state.mapMarkers.forEach(marker => {
				marker.setMap(null)
				if (marker.infoWindow) {
					marker.infoWindow.close()
				}
			})
		}

		// Si no hay registros para este día, solo limpiamos y salimos
		if (!records || records.length === 0) {
			this.setState({ mapMarkers: [] })
			return
		}

		const markers = []
		const bounds = new window.google.maps.LatLngBounds()

		records.forEach(record => {
			if (!record.latitude || !record.longitude) return

			// Buscar usuario por DNI en lugar de userId, convertir a string para consistencia
			const dni = record.dni ? String(record.dni) : null
			const user = dni ? (users[dni] || null) : null
			// Construir nombre completo, si no hay usuario mostrar solo el DNI
			let userName = 'Usuario desconocido'
			if (user && user.name) {
				const fullName = `${user.name || ''} ${user.lastName || ''}`.trim()
				userName = fullName || 'Usuario sin nombre'
			} else if (dni) {
				userName = `DNI: ${dni}`
			}
			const recordTypeLabel = this.getRecordTypeLabel(record.recordType)
			const formattedDate = this.formatDateForMap(record.dateLong)
			const observations = record.observations || 'Sin observaciones'

			let markerColor = '#ff4d4f'
			if (record.recordType === 'CHECK_IN') {
				markerColor = '#52c41a'
			} else if (record.recordType === 'RAINY_DAY') {
				markerColor = '#1890ff' // Azul para lluvia
			}

			// Path del icono location_on de Google Material Icons
			// ViewBox: 0 -960 960 960, el pin apunta hacia abajo
			const pinPath = 'M480-480q33 0 56.5-23.5T560-560q0-33-23.5-56.5T480-640q-33 0-56.5 23.5T400-560q0 33 23.5 56.5T480-480Zm0 400Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Z'

			const marker = new window.google.maps.Marker({
				position: { lat: record.latitude, lng: record.longitude },
				map: mapInstance,
				icon: {
					path: pinPath,
					fillColor: markerColor,
					fillOpacity: 1,
					strokeColor: '#fff',
					strokeWeight: 1.5,
					scale: 0.04,
					anchor: new window.google.maps.Point(480, 0)
				},
				title: `${recordTypeLabel} - ${userName}`
			})

			const infoWindowContent = `
				<div style="padding: 8px; min-width: 200px;">
					<div style="font-weight: bold; margin-bottom: 8px;">
						[${recordTypeLabel}] ${userName}
					</div>
					<div style="margin-bottom: 8px; color: #666;">
						${observations}
					</div>
					<div style="font-size: 12px;font-weight: bold;">
						${formattedDate}
					</div>
				</div>
			`

			const infoWindow = new window.google.maps.InfoWindow({
				content: infoWindowContent
			})

			marker.addListener('click', () => {
				// Cerrar otros info windows
				this.state.mapMarkers.forEach(m => {
					if (m.infoWindow) {
						m.infoWindow.close()
					}
				})
				infoWindow.open(mapInstance, marker)
			})

			marker.infoWindow = infoWindow
			markers.push(marker)
			bounds.extend({ lat: record.latitude, lng: record.longitude })
		})

		this.setState({ mapMarkers: markers })

		if (markers.length > 0) {
			mapInstance.fitBounds(bounds)
			// Reducir el zoom un poco para que se vea más alejado
			setTimeout(() => {
				const currentZoom = mapInstance.getZoom()
				if (currentZoom) {
					mapInstance.setZoom(currentZoom - 2)
				}
			}, 100)
			if (markers.length === 1) {
				mapInstance.setZoom(13)
			}
		}
	}

	loadRecordsForDate = (date) => {
		this.setState({ mapLoading: true })

		const startDate = this.getStartOfDay(date)
		const endDate = this.getEndOfDay(date)

		firestore.collection('records')
			.where('dateLong', '>=', startDate)
			.where('dateLong', '<=', endDate)
			.orderBy('dateLong')
			.get()
			.then(querySnapshot => {
				const recordsLoaded = []
				if (!querySnapshot.empty) {
					querySnapshot.forEach(doc => {
						const record = doc.data()
						recordsLoaded.push(record)
					})
				}
				this.setState({ mapRecords: recordsLoaded, mapLoading: false }, () => {
					if (this.state.mapInstance) {
						this.renderMarkers(recordsLoaded)
					}
				})
			})
			.catch(err => {
				console.log('Error getting records: ' + JSON.stringify(err))
				if (err.code === 'failed-precondition') {
					message.error('Error: Se requiere crear un índice en Firestore. Por favor, contacte al administrador.')
				} else {
					message.error('Error al cargar registros')
				}
				this.setState({ mapLoading: false })
			})
	}

	openMapModal = () => {
		const today = new Date()
		this.setState({
			mapModalVisible: true,
			mapDate: today,
			mapRecords: [],
			mapMarkers: []
		}, () => {
			this.loadRecordsForDate(today)
			setTimeout(() => {
				this.initializeMap()
			}, 100)
		})
	}

	closeMapModal = () => {
		// Limpiar marcadores
		if (this.state.mapMarkers && this.state.mapMarkers.length > 0) {
			this.state.mapMarkers.forEach(marker => {
				marker.setMap(null)
				if (marker.infoWindow) {
					marker.infoWindow.close()
				}
			})
		}

		this.setState({
			mapModalVisible: false,
			mapDate: new Date(),
			mapRecords: [],
			mapInstance: null,
			mapMarkers: []
		})
	}

	handleMapDateChange = (date) => {
		if (!date) return
		this.setState({ mapDate: date })
		this.loadRecordsForDate(date)
	}

	getColumnSearchProps = (dataIndex) => ({
		filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
			<div style={{ padding: 8 }}>
				<Input
					ref={node => {
						this.searchInput = node
					}}
					placeholder={`Buscar ${dataIndex === 'name' ? 'Nombre o Apellido' : dataIndex}`}
					value={selectedKeys[0]}
					onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
					onPressEnter={() => this.handleSearchColumn(selectedKeys, confirm, dataIndex)}
					style={{ width: 188, marginBottom: 8, display: 'block' }}
				/>
				<Space>
					<Button
						type="primary"
						onClick={() => this.handleSearchColumn(selectedKeys, confirm, dataIndex)}
						icon={<SearchOutlined />}
						size="small"
						style={{ width: 90 }}
					>
						Buscar
					</Button>
					<Button onClick={() => this.handleResetColumn(clearFilters)} size="small" style={{ width: 90 }}>
						Reset
					</Button>
				</Space>
			</div>
		),
		filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
		onFilter: (value, record) => {
			if (dataIndex === 'name') {
				const fullName = `${record.name || ''} ${record.lastName || ''}`.trim().toLowerCase()
				return fullName.includes(value.toLowerCase())
			}
			return record[dataIndex]
				? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
				: ''
		},
		onFilterDropdownVisibleChange: visible => {
			if (visible) {
				setTimeout(() => this.searchInput.select(), 100)
			}
		},
	})

	handleSearchColumn = (selectedKeys, confirm, dataIndex) => {
		confirm()
		this.setState({
			searchText: selectedKeys[0],
			searchedColumn: dataIndex,
		})
	}

	handleResetColumn = clearFilters => {
		clearFilters()
		this.setState({ searchText: '' })
	}

	openEditTimeModal = (record) => {
		this.setState({
			editTimeModalVisible: true,
			recordToEdit: record,
			newTimeValue: moment(record.dateLong)
		})
	}

	closeEditTimeModal = () => {
		this.setState({
			editTimeModalVisible: false,
			recordToEdit: null,
			newTimeValue: null
		})
	}

	handleTimeChange = (time) => {
		this.setState({ newTimeValue: time })
	}

	confirmEditTime = () => {
		const { recordToEdit, newTimeValue } = this.state
		if (!recordToEdit || !newTimeValue) return

		this.setState({ editingTimeLoading: true })

		// Construir nuevo dateLong mezclando fecha original y nueva hora
		const originalDate = new Date(recordToEdit.dateLong)
		const newDate = newTimeValue.toDate()

		originalDate.setHours(newDate.getHours(), newDate.getMinutes(), newDate.getSeconds())
		const newDateLong = originalDate.getTime()

		// Guardar el string compatible para facilitar lecturas
		const newDateString = moment(newDateLong).format('D/M/YYYY, h:mm:ss a')

		firestore.collection('records').doc(recordToEdit.id).update({
			dateLong: newDateLong,
			dateString: newDateString
		})
			.then(() => {
				message.success('Hora modificada correctamente')
				this.setState({ editingTimeLoading: false, editTimeModalVisible: false })
				this.handleSearch() // Recargar registros
			})
			.catch(err => {
				console.log('Error updating record: ', err)
				message.error('Error al modificar la hora')
				this.setState({ editingTimeLoading: false })
			})
	}

	render() {
		const {
			dateRange,
			tableData,
			loading,
			imageModalVisible,
			imageModalUrl,
			imageModalLoading,
			mapModalVisible,
			mapCoordinates
		} = this.state

		const mainColumns = [
			{
				title: '',
				dataIndex: 'status',
				key: 'status',
				align: 'center',
				width: 60,
				filters: [
					{ text: 'Completado', value: 'green' },
					{ text: 'Sin Egreso', value: 'yellow' },
					{ text: 'Sin Registros', value: 'red' }
				],
				onFilter: (value, record) => record.status === value,
				render: (status) => {
					let color = '#52c41a' // verde
					if (status === 'yellow') color = '#faad14' // amarillo
					if (status === 'red') color = '#ff4d4f' // rojo
					return <span style={{ color, fontSize: '28px', lineHeight: '15px', position: 'relative', top: '-1px' }} title={status === 'green' ? 'Completado' : (status === 'yellow' ? 'Falta Egreso' : 'Sin Registros')}>●</span>
				}
			},
			{
				title: 'Nombre y Apellido',
				dataIndex: 'name',
				key: 'name',
				align: 'center',
				...this.getColumnSearchProps('name'),
				render: (text, record) => `${record.name} ${record.lastName}`.trim()
			},
			{
				title: 'Correo',
				dataIndex: 'email',
				key: 'email',
				align: 'center'
			},
			{
				title: 'Categoría',
				dataIndex: 'category',
				key: 'category',
				align: 'center',
				render: (text) => text && Constants.OPERATOR_CATEGORIES[text] ? Constants.OPERATOR_CATEGORIES[text] : text || '--'
			},
			{
				title: 'Horas trabajadas',
				dataIndex: 'workedHours',
				key: 'workedHours',
				align: 'center',
				render: (hours) => `${hours} hs`
			},
			{
				title: 'Horas extras',
				dataIndex: 'extraHours',
				key: 'extraHours',
				align: 'center',
				render: (hours) => `${hours || 0} hs`
			},
			{
				title: 'Horas traslado',
				dataIndex: 'travelHours',
				key: 'travelHours',
				align: 'center',
				render: (hours) => `${hours || 0} hs`
			}
		]

		const expandedRowRender = (record) => {
			const expandedColumns = [
				{
					title: 'Tipo de registro',
					dataIndex: 'recordType',
					key: 'recordType',
					align: 'center',
					render: (recordType) => <span style={{ fontWeight: 'bold' }}>{this.getRecordTypeLabel(recordType)}</span>
				},
				{
					title: 'Fecha de carga',
					dataIndex: 'dateLong',
					key: 'dateLong',
					align: 'center',
					render: (dateLong) => this.formatDate(dateLong)
				},
				{
					title: 'Día',
					dataIndex: 'dateLong',
					key: 'day',
					align: 'center',
					render: (dateLong) => this.getDayName(dateLong)
				},
				{
					title: 'Observación',
					dataIndex: 'observations',
					key: 'observations',
					align: 'center',
					render: (observations) => {
						const text = observations || '--'
						return (
							<Tooltip title={text}>
								<span style={{
									display: 'inline-block',
									maxWidth: '200px',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap'
								}}>
									{text}
								</span>
							</Tooltip>
						)
					}
				},
				{
					title: 'Imagen',
					key: 'image',
					align: 'center',
					render: (_, record) => {
						return (
							<ImageThumbnail
								imagePath={record.imagePath}
								onImageClick={this.showImageModal}
							/>
						)
					}
				},
				{
					title: 'Ubicación',
					key: 'location',
					align: 'center',
					render: (_, record) => {
						if (record.latitude && record.longitude) {
							return (
								<EnvironmentOutlined
									style={{ fontSize: '20px', cursor: 'pointer', color: '#52c41a' }}
									onClick={() => this.showMapModal(record.latitude, record.longitude)}
								/>
							)
						}
						return '--'
					}
				},
				{
					title: 'Proyecto detectado',
					dataIndex: 'detectedMainProject',
					key: 'detectedMainProject',
					align: 'center',
					render: (mainProject, record) => {
						// Mostrar proyecto para CHECK_IN y CHECK_OUT
						if (mainProject) {
							return mainProject.name || '--'
						} else if (record.detectedSubProject) {
							return '--'
						} else if (record.detectedProject) {
							return record.detectedProject.name || '--'
						}
						return '--'
					}
				},
				{
					title: 'Subproyecto detectado',
					dataIndex: 'detectedSubProject',
					key: 'detectedSubProject',
					align: 'center',
					render: (subProject) => {
						if (subProject) {
							return subProject.name || '-'
						}
						return '-'
					}
				},
				{
					title: '',
					key: 'edit',
					align: 'center',
					render: (_, record) => {
						if (record.recordType === 'CHECK_OUT' || record.recordType === 'CHECK_IN') {
							return (
								<Button
									type="primary"
									size="small"
									style={{ backgroundColor: '#faad14', borderColor: '#faad14', padding: '0 10px' }}
									onClick={() => this.openEditTimeModal(record)}
								>
									Modificar hora
								</Button>
							)
						}
						return null
					}
				}
			]

			return (
				<Table
					columns={expandedColumns}
					dataSource={record.records}
					pagination={false}
					rowKey={(r) => `${r.dni || 'unknown'}-${r.dateLong}`}
					rowClassName={(r) => {
						// Pintar de ambar las filas internas sin proyecto
						const needsProject = r.recordType === 'CHECK_IN' || r.recordType === 'CHECK_OUT' || r.recordType === 'RAINY_DAY'
						if (needsProject && !r.detectedProject) return 'row-missing-project'
						return ''
					}}
					size="small"
				/>
			)
		}

		return (
			<div>
				<Grid>
					<Divider>Registros</Divider>
					<Row center="xs" style={{ marginBottom: '20px', marginTop: '20px' }}>
						<Col xs={12} sm={8} md={6}>
							<RangePicker
								format="DD/MM/YYYY"
								value={dateRange}
								onChange={this.handleDateRangeChange}
								style={{ width: '100%', marginRight: '10px' }}
							/>
						</Col>
						<Col xs={12} sm={4} md={2}>
							<Button
								type="primary"
								onClick={this.handleSearch}
								loading={loading}
							>
								Buscar
							</Button>
						</Col>
					</Row>
					<Row style={{ marginBottom: '20px' }}>
						<Col xs={12} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
							<Button
								type="default"
								icon={<DownloadOutlined />}
								onClick={this.exportToExcel}
							>
								Descargar Excel
							</Button>
							<Button
								type="default"
								icon={<EnvironmentOutlined />}
								onClick={this.openMapModal}
							>
								Ver Registros por día
							</Button>
						</Col>
					</Row>
					<Row center="xs" style={{ marginBottom: '20px' }}>
						<Col xs={12}>
							<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '25px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
								<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									<span style={{ color: '#52c41a', fontSize: '28px', lineHeight: '15px', position: 'relative', top: '-2px' }}>●</span>
									<span style={{ fontSize: '15px', fontWeight: 500 }}>Ingreso y Egreso</span>
								</span>
								<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									<span style={{ color: '#faad14', fontSize: '28px', lineHeight: '15px', position: 'relative', top: '-2px' }}>●</span>
									<span style={{ fontSize: '15px', fontWeight: 500 }}>Sin Egreso</span>
								</span>
								<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									<span style={{ color: '#ff4d4f', fontSize: '28px', lineHeight: '15px', position: 'relative', top: '-2px' }}>●</span>
									<span style={{ fontSize: '15px', fontWeight: 500 }}>Sin Registros</span>
								</span>
							</div>
						</Col>
					</Row>
					<Row>
						<Col xs={12}>
							<Table
								columns={mainColumns}
								dataSource={tableData}
								loading={loading}
								expandable={{
									expandedRowRender: expandedRowRender
								}}
								rowKey="dni"
								rowClassName={(record) => record.hasRecordsWithoutProject ? 'row-missing-project' : ''}
								pagination={{ pageSize: 20, showSizeChanger: false }}
								scroll={{ y: 600 }}
							/>
						</Col>
					</Row>
				</Grid>

				<Modal
					title="Imagen del registro"
					visible={imageModalVisible}
					onCancel={this.closeImageModal}
					footer={null}
					width={800}
					maskClosable={false}
					keyboard={false}
				>
					{imageModalLoading ? (
						<div style={{ textAlign: 'center', padding: '50px' }}>
							<Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
						</div>
					) : (
						<img
							alt="Registro"
							src={imageModalUrl}
							style={{ width: '100%' }}
						/>
					)}
				</Modal>

				<Modal
					title="Ubicación del registro"
					visible={mapModalVisible && mapCoordinates && !this.state.mapDate}
					onCancel={this.closeSingleLocationModal}
					footer={null}
					width={800}
					maskClosable={false}
					keyboard={false}
				>
					{mapCoordinates && (
						<iframe
							width="100%"
							height="450"
							style={{ border: 0 }}
							src={`https://www.google.com/maps/embed/v1/place?key=${Constants.GOOGLE_API.MAPS_API_KEY}&q=${mapCoordinates.latitude},${mapCoordinates.longitude}&zoom=15`}
							allowFullScreen
						/>
					)}
				</Modal>

				<Modal
					title="Mapa de Registros por Día"
					visible={this.state.mapModalVisible && !!this.state.mapDate}
					onCancel={this.closeMapModal}
					footer={null}
					width="100%"
					style={{ top: 0, paddingBottom: 0, maxWidth: '100%' }}
					bodyStyle={{
						height: 'calc(100vh - 55px)',
						padding: '20px',
						position: 'relative',
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden'
					}}
					maskClosable={false}
					keyboard={false}
					closable={true}
				>
					<div style={{ marginBottom: '20px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
						<DatePicker
							format="DD/MM/YYYY"
							value={this.state.mapDate ? moment(this.state.mapDate) : null}
							onChange={(date) => this.handleMapDateChange(date ? date.toDate() : null)}
							style={{ width: '200px' }}
							disabled={this.state.mapLoading}
						/>
					</div>
					{this.state.mapLoading && (
						<div style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: 'rgba(255, 255, 255, 0.8)',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							zIndex: 1000
						}}>
							<Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
						</div>
					)}
					<div
						id="map-container"
						ref={el => { this.mapContainerRef = el }}
						style={{
							width: '100%',
							height: '100%',
							flex: 1,
							minHeight: 0
						}}
					/>
				</Modal>

				<Modal
					title={<span style={{ fontWeight: 600, fontSize: '1.2rem' }}>Ajustar Hora {this.state.recordToEdit?.recordType === 'CHECK_IN' ? 'de Ingreso' : 'de Egreso'}</span>}
					visible={this.state.editTimeModalVisible}
					onOk={this.confirmEditTime}
					onCancel={this.closeEditTimeModal}
					confirmLoading={this.state.editingTimeLoading}
					okText="Guardar"
					cancelText="Cancelar"
					maskClosable={false}
					bodyStyle={{ padding: '24px' }}
					className="edit-time-modal"
				>
					<div style={{ padding: '10px 0', textAlign: 'center' }}>
						<p style={{ marginBottom: 20, color: '#595959', fontSize: '1rem' }}>
							Seleccione la nueva hora para el {this.state.recordToEdit?.recordType === 'CHECK_IN' ? 'ingreso' : 'egreso'} correspondiente
						</p>
						<TimePicker
							value={this.state.newTimeValue}
							onChange={this.handleTimeChange}
							onSelect={(time) => {
								const prevTime = this.state.newTimeValue
								this.setState({ newTimeValue: time }, () => {
									// Cierra el picker solo si la "hora" fue la misma, asumiendo interacción sobre los "minutos"
									if (prevTime && prevTime.hours() === time.hours()) {
										if (this.timePickerRef) {
											this.timePickerRef.blur()
										}
									}
								})
							}}
							ref={el => this.timePickerRef = el}
							format="HH:mm"
							showNow={false}
							allowClear={false}
							style={{ width: 160, fontSize: '1.1rem' }}
							size="large"
							popupClassName="hide-timepicker-footer"
						/>
					</div>
				</Modal>
			</div>
		)
	}
}

Registers.propTypes = {}

const mapStateToProps = state => ({
	user: getUser(state)
})

export default connect(mapStateToProps)(Registers)

